import { useState } from 'react';

import type { FundPolicy } from '../../core/fundPolicy/schema';
import { composeChildName } from '../../features/employees/application/childName';
import {
  childrenShortfall,
  needsOnlyFirstName,
} from '../../features/employees/application/dependentForm';
import {
  RELATION_LABELS_AR,
  STATUS_LABELS_AR,
  STATUS_TONES,
  isEditable,
} from '../../features/employees/application/dependentLabels';
import { useMyDependents } from '../../features/employees/application/useMyDependents';
import { DependentsPage } from '../../pages/DependentsPage';

import { useServices } from '../providers/servicesContext';

/**
 * Connects the dependants screen to its ports.
 *
 * The page itself stays presentational and testable without Firebase; this is
 * the only piece that knows a port exists (§5.4).
 */
export function DependentsScreen({
  policy,
  declaredChildrenCount,
  today,
  employeeFullName,
}: {
  readonly policy: FundPolicy;
  readonly declaredChildrenCount: number;
  readonly today: string;
  readonly employeeFullName: string;
}) {
  const { dependents: port } = useServices();
  const controller = useMyDependents(port, policy, true, today, employeeFullName);
  const [candidateName, setCandidateName] = useState('');

  // Only relations the committee has enabled are offered. A disabled relation
  // left in the list would be rejected on save with no explanation.
  const relationOptions = policy.dependentRules
    .filter((rule) => rule.allowed)
    .map((rule) => ({ value: rule.relation, labelAr: RELATION_LABELS_AR[rule.relation] }));

  return (
    <DependentsPage
      dependents={controller.dependents.map((dependent) => ({
        id: dependent.id,
        fullName: dependent.fullName,
        relationLabelAr: RELATION_LABELS_AR[dependent.relation],
        birthDate: dependent.birthDate,
        statusLabelAr: STATUS_LABELS_AR[dependent.status],
        statusTone: STATUS_TONES[dependent.status],
        relation: dependent.relation,
        gender: dependent.gender,
        nationalId: dependent.nationalId,
        editable: isEditable(dependent.status),
      }))}
      relationOptions={relationOptions}
      childrenShortfall={childrenShortfall(controller.dependents, declaredChildrenCount)}
      canAddMore={controller.canAddMore}
      errors={controller.errors}
      ineligibleReasonsAr={controller.ineligibleReasonsAr}
      requiresProofDocument={controller.requiresProofDocument}
      onValidate={(values) => {
        // Kept so the screen can show what a child will be recorded as while
        // the first name is still being typed.
        setCandidateName(needsOnlyFirstName(values) ? values.fullName : '');
        controller.validate(values);
      }}
      onAdd={controller.add}
      onUpdate={controller.update}
      errorAr={controller.errorAr}
      submissionsClosed={!policy.employeeSubmissionsEnabled}
      derivedChildNameAr={composeChildName(candidateName, employeeFullName)?.fullName ?? null}
      employeeFullName={employeeFullName}
    />
  );
}
